import React from "react";
import {
  Box,
  Divider,
  Grid,
  Link as MLink,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import Seo from "../../components/Seo/Seo";
import { PAGE_SEO } from "../../seo/seoConstants";
import Footer from "../../components/Footer/Footer";
import { SECTION_SX, CONTACT_EMAIL } from "../shared/constants";
import { CommonBoxStyles, AnchorSpan } from "../shared/styledComponents";

const Anchor: React.FC<{ id: string }> = ({ id }) => <AnchorSpan id={id} />;

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <PageLayout>
      <Seo {...PAGE_SEO["/politica-de-privacidad"]} />
      <CommonBoxStyles>
        <Box px={2}>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item md={3} sx={{ display: { xs: "none", md: "block" } }}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Contenido
                </Typography>
                <List dense>
                  {[
                    { href: "#quienes-somos", label: "Quiénes somos" },
                    { href: "#datos", label: "Datos que recopilamos" },
                    { href: "#finalidad", label: "Finalidad del tratamiento" },
                    { href: "#base-legal", label: "Base legal" },
                    { href: "#terceros", label: "Proveedores y terceros" },
                    { href: "#conservacion", label: "Conservación" },
                    { href: "#derechos", label: "Tus derechos" },
                    { href: "#seguridad", label: "Seguridad" },
                    { href: "#transferencias", label: "Transferencias internacionales" },
                    { href: "#cookies", label: "Cookies" },
                    { href: "#cambios", label: "Cambios a esta política" },
                    { href: "#contacto", label: "Contacto" },
                  ].map((item) => (
                    <ListItem key={item.href} disablePadding>
                      <MLink
                        href={item.href}
                        underline="hover"
                        color="inherit"
                        sx={{ width: "100%" }}
                      >
                        <ListItemButton sx={{ borderRadius: 1 }}>
                          <ListItemText
                            primaryTypographyProps={{ variant: "body2" }}
                            primary={item.label}
                          />
                        </ListItemButton>
                      </MLink>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={9}>
              <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  Política de Privacidad
                </Typography>
                <Typography
                  variant="body2"
                  fontStyle={"italic"}
                  color="text.primary"
                  sx={{ mt: 0.5 }}
                >
                  Última actualización: 4 de agosto de 2026
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  Bienvenido(a) a Entorno Biai (la «App»). Esta Política de Privacidad explica cómo
                  recopilamos, usamos y protegemos tu información cuando inicias sesión y utilizas
                  nuestras funcionalidades para visualizar productos, agendar servicios online y
                  guardar el historial de tus compras o actividades educativas.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Anchor id="quienes-somos" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  1. Quiénes somos
                </Typography>
                <Typography variant="body1">
                  Entorno Biai es una aplicación web cuyo objetivo es mostrar productos, ofrecer
                  servicios online como la reparación de celulares o computadores, y permitir a los
                  usuarios autenticados guardar y consultar las compras o servicios agendados que
                  realizaron mediante la App. Además, proyectamos ofrecer una interfaz para usuarios
                  que participan en procesos educativos con nosotros.
                </Typography>
                <Anchor id="datos" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  2. Datos que recopilamos
                </Typography>
                <Typography variant="body1">
                  Cuando inicias sesión con Google (a través de Auth0) recibimos únicamente
                  información básica necesaria para identificar tu cuenta dentro de la App:
                </Typography>
                <List sx={{ listStyleType: "disc", pl: 3 }}>
                  <ListItem sx={{ display: "list-item" }}>
                    Nombre y foto de perfil (<code>userinfo.profile</code>).
                  </ListItem>
                  <ListItem sx={{ display: "list-item" }}>
                    Correo electrónico principal (<code>userinfo.email</code>).
                  </ListItem>
                  <ListItem sx={{ display: "list-item" }}>
                    Identificador abierto (<code>openid</code>) para establecer sesión.
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  No solicitamos datos sensibles ni permisos adicionales a los mencionados.
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, fontWeight: 700 }}>
                  Si navegas sin iniciar sesión
                </Typography>
                <Typography variant="body1">
                  No sabemos quién eres y no recibimos ninguno de los datos anteriores. Aun así,{" "}
                  <strong>si aceptas las cookies de analítica</strong> se genera un identificador
                  aleatorio de navegador (la cookie <strong>_ga</strong>) y Google Analytics
                  registra tu dirección IP, el tipo de dispositivo y las páginas que visitas. Ese
                  identificador no lleva tu nombre, pero permite reconocer el mismo navegador entre
                  visitas, y por eso te pedimos autorización antes de activarlo. Si no las aceptas
                  no se genera ese identificador y no podemos reconocerte entre una visita y otra.
                </Typography>
                <Anchor id="finalidad" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  3. Finalidad del tratamiento
                </Typography>
                <List sx={{ listStyleType: "disc", pl: 3 }}>
                  <ListItem sx={{ display: "list-item" }}>
                    Mostrar tu información de perfil (nombre y foto) en la App.
                  </ListItem>
                  <ListItem sx={{ display: "list-item" }}>
                    Identificar tu cuenta y permitirte acceder a tus compras guardadas o servicios
                    agendados.
                  </ListItem>
                  <ListItem sx={{ display: "list-item" }}>
                    Enviarte notificaciones cuando realizas compras, agendas servicios o participas
                    en actividades educativas.
                  </ListItem>
                  <ListItem sx={{ display: "list-item" }}>
                    Enviarte comunicaciones operativas relacionadas con tu cuenta.
                  </ListItem>
                </List>
                <Anchor id="base-legal" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  4. Base legal
                </Typography>
                <Typography variant="body1">
                  Tratamos tus datos sobre la base de tu consentimiento otorgado al iniciar sesión
                  con Google y conforme a la relación contractual/precumplimiento necesaria para
                  prestarte el servicio.
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Las cookies de analítica tienen una base legal distinta e independiente: tu
                  autorización expresa en el aviso de cookies. No dependen de que inicies sesión, y
                  puedes retirarla en cualquier momento sin que eso afecte tu cuenta ni tus pedidos.
                </Typography>
                <Anchor id="terceros" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  5. Proveedores y terceros
                </Typography>
                <Typography variant="body1">
                  Utilizamos <strong>Auth0</strong> como proveedor de identidad para gestionar el
                  inicio de sesión con Google. Auth0 procesa tus datos únicamente para autenticación
                  y gestión de sesiones.
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Si aceptas las cookies de analítica, utilizamos{" "}
                  <strong>Google Analytics 4</strong> para medir el uso de la tienda. Google procesa
                  esos datos como encargado del tratamiento y por nuestra cuenta. Si no las aceptas,
                  no se carga y no recibe nada; puedes revisar el detalle y cambiar tu decisión en
                  la sección{" "}
                  <MLink component={Link} to="/politica-de-privacidad#cookies">
                    Cookies
                  </MLink>
                  .
                </Typography>
                <Anchor id="conservacion" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  6. Conservación
                </Typography>
                <Typography variant="body1">
                  Conservamos tus datos mientras mantengas tu cuenta activa o durante el tiempo
                  necesario para cumplir obligaciones legales aplicables. Puedes solicitar la
                  eliminación cuando lo desees.
                </Typography>
                <Anchor id="derechos" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  7. Tus derechos
                </Typography>
                <Typography variant="body1">
                  Tienes derecho a acceder, actualizar, rectificar y eliminar tus datos; así como a
                  oponerte o solicitar la limitación del tratamiento y la portabilidad en los
                  términos previstos por la ley. Puedes retirar tu consentimiento en cualquier
                  momento.
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, fontWeight: 700 }}>
                  Procedimiento para ejercerlos
                </Typography>
                <Typography variant="body1">
                  El área de Atención al Cliente de Entorno Biai S.A.S. es la responsable de atender
                  peticiones, consultas y reclamos. Escríbenos a{" "}
                  <MLink href={`mailto:${CONTACT_EMAIL}`} underline="hover">
                    {CONTACT_EMAIL}
                  </MLink>{" "}
                  indicando tu nombre, un dato de contacto para responderte, la descripción de lo
                  que solicitas y los documentos que quieras aportar. Si el mensaje está incompleto,
                  te lo haremos saber dentro de los cinco (5) días hábiles siguientes para que lo
                  completes.
                </Typography>
                <List sx={{ listStyleType: "disc", pl: 3 }}>
                  <ListItem sx={{ display: "list-item" }}>
                    <strong>Consultas</strong> (conocer qué datos tenemos y con qué finalidad): se
                    atienden en un máximo de diez (10) días hábiles. Si no fuera posible, te
                    informaremos el motivo y la nueva fecha, que no superará los cinco (5) días
                    hábiles siguientes al vencimiento del primer plazo.
                  </ListItem>
                  <ListItem sx={{ display: "list-item" }}>
                    <strong>Reclamos</strong> (corregir, actualizar, suprimir o revocar la
                    autorización): se atienden en un máximo de quince (15) días hábiles contados
                    desde el día siguiente a su recepción. Si no fuera posible, te informaremos el
                    motivo y la nueva fecha, que no superará los ocho (8) días hábiles siguientes al
                    vencimiento del primer plazo.
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Si consideras que no atendimos tu solicitud, puedes acudir a la{" "}
                  <strong>Superintendencia de Industria y Comercio</strong>, autoridad de protección
                  de datos personales en Colombia. Ten en cuenta que la ley exige haber agotado
                  primero el trámite de consulta o reclamo ante nosotros.
                </Typography>
                <Anchor id="seguridad" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  8. Seguridad
                </Typography>
                <Typography variant="body1">
                  Aplicamos medidas razonables de seguridad (por ejemplo, cifrado en tránsito y
                  controles de acceso) para proteger la información. No obstante, ningún sistema es
                  completamente seguro.
                </Typography>
                <Anchor id="transferencias" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  9. Transferencias internacionales
                </Typography>
                <Typography variant="body1">
                  Tus datos pueden procesarse fuera de tu país de residencia, dependiendo de la
                  ubicación de nuestros proveedores (p. ej., Auth0 y Google). Adoptamos salvaguardas
                  adecuadas cuando es necesario.
                </Typography>
                <Anchor id="cookies" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  10. Cookies
                </Typography>
                <Typography variant="body1">
                  Usamos dos tipos de cookies y almacenamiento local, y solo uno de ellos requiere
                  tu autorización.
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, fontWeight: 700 }}>
                  Necesarias (siempre activas)
                </Typography>
                <Typography variant="body1">
                  Mantienen tu sesión iniciada, conservan los productos de tu carrito y recuerdan el
                  municipio de entrega que seleccionaste. Se guardan en el almacenamiento local de
                  tu navegador, no salen de tu dispositivo y sin ellas la tienda no puede funcionar,
                  por lo que no dependen de tu autorización.
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, fontWeight: 700 }}>
                  Analítica (solo si las aceptas)
                </Typography>
                <Typography variant="body1">
                  Usamos Google Analytics 4 para saber qué productos se consultan y en qué paso se
                  abandona un pedido. Instala las cookies <strong>_ga</strong> y{" "}
                  <strong>_ga_&lt;ID&gt;</strong> en este dominio, con una duración aproximada de
                  dos años, y registra un identificador interno de tu cuenta cuando has iniciado
                  sesión — <strong>nunca tu nombre, tu correo ni tu número de teléfono</strong>.
                  Google actúa como encargado del tratamiento; consulta también la sección{" "}
                  <MLink component={Link} to="/politica-de-privacidad#terceros">
                    Proveedores y terceros
                  </MLink>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  <strong>
                    Mientras no las aceptes no instalamos ninguna cookie de analítica ni generamos
                    ningún identificador de tu navegador
                  </strong>
                  , de modo que no es posible reconocerte entre una visita y otra. Google sí recibe
                  un registro básico y anónimo de la página consultada, junto con tu dirección IP,
                  que no se asocia a ningún identificador ni permite construir un historial tuyo.
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Puedes aceptarlas o rechazarlas en el aviso que aparece en tu primera visita, y
                  cambiar tu decisión en cualquier momento desde el enlace{" "}
                  <strong>Preferencias de cookies</strong> que está al final de cada página. Si las
                  rechazas después de haberlas aceptado, eliminamos las cookies ya instaladas.
                </Typography>
                <Anchor id="cambios" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  11. Cambios a esta política
                </Typography>
                <Typography variant="body1">
                  Podemos actualizar esta Política de Privacidad ocasionalmente. Publicaremos la
                  versión vigente en esta misma página e indicaremos la fecha de última
                  actualización. Los cambios sustanciales se comunicarán antes de su entrada en
                  vigencia por los medios de contacto que tengamos registrados.
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  <strong>Entrada en vigencia:</strong> 4 de agosto de 2026.{" "}
                  <strong>Período de vigencia:</strong> indefinido mientras se mantenga publicada en
                  esta página; las bases de datos se conservarán durante la vigencia de la relación
                  con el titular y, después, durante los términos legales aplicables.
                </Typography>
                <Anchor id="contacto" />
                <Typography variant="h6" sx={{ fontWeight: 700, ...SECTION_SX }}>
                  12. Contacto
                </Typography>
                <Typography variant="body1">
                  Responsable del tratamiento de tus datos personales:
                </Typography>
                <List sx={{ listStyleType: "disc", pl: 3 }}>
                  <ListItem sx={{ display: "list-item" }}>
                    <strong>Razón social:</strong> Entorno Biai S.A.S.
                  </ListItem>
                  <ListItem sx={{ display: "list-item" }}>
                    <strong>NIT:</strong> 901753539-7
                  </ListItem>
                  <ListItem sx={{ display: "list-item" }}>
                    <strong>Domicilio y dirección:</strong> Cl. 18 #20-71, Yarumal, Antioquia,
                    Colombia.
                  </ListItem>
                  <ListItem sx={{ display: "list-item" }}>
                    <strong>Correo electrónico:</strong>{" "}
                    <MLink href={`mailto:${CONTACT_EMAIL}`} underline="hover">
                      {CONTACT_EMAIL}
                    </MLink>
                  </ListItem>
                  <ListItem sx={{ display: "list-item" }}>
                    <strong>Teléfono:</strong>{" "}
                    <MLink href="https://wa.me/573128014133" underline="hover">
                      +57 312 801 4133
                    </MLink>
                  </ListItem>
                  <ListItem sx={{ display: "list-item" }}>
                    <strong>Área responsable de peticiones, consultas y reclamos:</strong> Atención
                    al Cliente, en el mismo correo y teléfono.
                  </ListItem>
                </List>
                <Divider sx={{ my: 3 }} />
                <Typography variant="body2" color="text.primary">
                  Enlaces útiles:{" "}
                  <MLink
                    href="https://entornobiai.com"
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                  >
                    Página principal
                  </MLink>
                  {" • "}
                  <Link to="/condiciones-del-servicio" style={{ textDecoration: "none" }}>
                    <MLink component="span" underline="hover">
                      Condiciones del Servicio
                    </MLink>
                  </Link>
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Footer>
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} Entorno Biai
            </Typography>
          </Footer>
        </Box>
      </CommonBoxStyles>
    </PageLayout>
  );
};

export default PrivacyPolicyPage;
